import { useState } from 'react';
import { Plus, Save, Trash2, Download, Check, Search, Mail, Lock, Sparkles, Rows3, Sliders } from 'lucide-react';
import { useToast } from '../../../components/ui/Toast';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { GroupedSelect } from '../../../components/ui/GroupedSelect';
import { Checkbox } from '../../../components/ui/Checkbox';
import { RadioGroup } from '../../../components/ui/Radio';
import { Toggle } from '../../../components/ui/Toggle';
import { FormField } from '../../../components/ui/FormField';
import { ToggleRow } from '../../../components/ui/ToggleRow';
import { Slider, BreakpointSlider } from '../../../components/ui/Slider';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { CategoryAutoActionRow } from '../../../components/ui/CategoryAutoActionRow';
import { CodeSnippet } from '../components/CodeSnippet';
import { Subsection } from '../components/Subsection';

// ============================================================================
// Sekcja: Buttony
// ============================================================================

export function ButtonsSection() {
  const [clickedLoading, setClickedLoading] = useState(false);

  const handleLoadingDemo = () => {
    setClickedLoading(true);
    setTimeout(() => setClickedLoading(false), 1500);
  };

  const variants: { variant: 'primary' | 'secondary' | 'ghost' | 'danger'; label: string }[] = [
    { variant: 'primary',   label: 'Primary' },
    { variant: 'secondary', label: 'Secondary' },
    { variant: 'ghost',     label: 'Ghost' },
    { variant: 'danger',    label: 'Danger' },
  ];

  return (
    <div className="space-y-10">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Komponent <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Button>'}</code>{' '}
        z <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">@/components/ui/Button</code>.
        Warianty: primary / secondary / ghost / danger / icon. Rozmiary: sm / md / lg. Stany:
        normal, hover, focus, disabled, loading. Dla linków stylizowanych jak button używaj{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">buttonVariants()</code>.
      </p>

      <CodeSnippet
        language="tsx"
        code={`import { Button } from '@/components/ui/Button';

<Button variant="primary" leftIcon={<Save />}>Zapisz</Button>
<Button variant="secondary" size="sm">Eksportuj</Button>
<Button variant="ghost" onClick={onClose}>Anuluj</Button>
<Button variant="danger" leftIcon={<Trash2 />}>Usuń</Button>
<Button variant="icon" leftIcon={<Edit3 />} aria-label="Edytuj" />`}
      />

      <Subsection n={1} title="Warianty × rozmiary">
        <div className="rounded-xl border border-border bg-surface-raised/40 overflow-hidden">
          <div className="grid grid-cols-[140px,1fr,1fr,1fr] gap-4 items-center px-4 py-3 bg-surface-base/40 border-b border-border-subtle">
            <span className="text-xs font-mono text-text-muted">variant</span>
            <span className="text-xs font-mono text-text-muted">size="sm"</span>
            <span className="text-xs font-mono text-text-muted">size="md" (default)</span>
            <span className="text-xs font-mono text-text-muted">size="lg"</span>
          </div>
          {variants.map(v => (
            <div key={v.variant} className="grid grid-cols-[140px,1fr,1fr,1fr] gap-4 items-center px-4 py-3 border-b border-border-subtle last:border-0">
              <code className="text-xs font-mono text-text-primary font-semibold">{v.variant}</code>
              <div><Button variant={v.variant} size="sm">{v.label}</Button></div>
              <div><Button variant={v.variant} size="md">{v.label}</Button></div>
              <div><Button variant={v.variant} size="lg">{v.label}</Button></div>
            </div>
          ))}
        </div>
      </Subsection>

      <Subsection n={2} title="Stany">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <Button variant="primary">Normal</Button>
            <Button variant="primary" disabled>Disabled</Button>
            <Button variant="primary" loading>Loading</Button>
            <Button variant="primary" onClick={handleLoadingDemo} loading={clickedLoading}>
              {clickedLoading ? 'Zapisywanie...' : 'Kliknij - demo loading'}
            </Button>
          </div>
          <p className="text-[11px] text-text-disabled mt-3">
            Focus ring widoczny przy Tab navigation. Loading = spinner zamiast leftIcon + disabled (przechwytuje klik).
          </p>
        </div>
      </Subsection>

      <Subsection n={3} title="Z ikonami">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <Button variant="primary" leftIcon={<Plus />}>Dodaj</Button>
            <Button variant="primary" leftIcon={<Save />}>Zapisz</Button>
            <Button variant="secondary" leftIcon={<Download />}>Pobierz</Button>
            <Button variant="danger" leftIcon={<Trash2 />}>Usuń</Button>
            <Button variant="ghost" rightIcon={<Check />}>Potwierdź</Button>
            <Button variant="primary" leftIcon={<Search />} rightIcon={<Check />}>Left + Right</Button>
          </div>
        </div>
      </Subsection>

      <Subsection n={4} title='Icon-only (variant="icon")'>
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <Button variant="icon" size="sm" leftIcon={<Plus />} aria-label="Dodaj" />
            <Button variant="icon" size="md" leftIcon={<Save />} aria-label="Zapisz" />
            <Button variant="icon" size="lg" leftIcon={<Trash2 />} aria-label="Usuń" />
            <Button variant="icon" size="md" leftIcon={<Search />} disabled aria-label="Szukaj" />
          </div>
          <p className="text-[11px] text-text-disabled mt-3">
            Dla icon-only OBOWIĄZKOWO <code className="px-1 rounded bg-surface-raised text-text-secondary">aria-label</code> (accessibility).
          </p>
        </div>
      </Subsection>

      <Subsection n={5} title="Full width">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 max-w-md">
          <Button variant="primary" fullWidth leftIcon={<Save />}>Zapisz ustawienia</Button>
        </div>
        <p className="text-[11px] text-text-disabled mt-2">
          Używaj dla sticky save bar i form submitów na mobile.
        </p>
      </Subsection>

      <Subsection n={6} title="buttonVariants() dla linków" description="Helper zwracający className dla <Link> stylizowanego jak button.">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-3">
          <CodeSnippet
            language="tsx"
            code={`import { buttonVariants } from '@/components/ui/Button';

<Link to="/settings" className={buttonVariants({ variant: 'primary' })}>
  Ustawienia
</Link>`}
          />
        </div>
      </Subsection>

    </div>
  );
}

// ============================================================================
// Sekcja: Inputs
// ============================================================================

export function InputsSection() {
  const [searchVal, setSearchVal] = useState('clearable example');
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Komponent <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Input>'}</code>.
        Obsługuje wszystkie typy HTML (text/email/number/password/date/search), ikony po bokach,
        clearable, stan błędu. Do walidacji i labelowania używaj{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<FormField>'}</code>.
      </p>

      <CodeSnippet
        language="tsx"
        code={`import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';

<FormField label="Email" htmlFor="email" error={errors.email}>
  <Input id="email" type="email" leftIcon={<Mail />} />
</FormField>

<Input type="search" leftIcon={<Search />} clearable value={q} onChange={...} onClear={...} />`}
      />

      <Subsection n={1} title="Typy">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Text" htmlFor="demo-text">
            <Input id="demo-text" type="text" placeholder="Jan Kowalski" />
          </FormField>
          <FormField label="Email" htmlFor="demo-email">
            <Input id="demo-email" type="email" placeholder="jan@example.com" leftIcon={<Mail />} />
          </FormField>
          <FormField label="Password" htmlFor="demo-password">
            <Input id="demo-password" type="password" placeholder="••••••••" leftIcon={<Lock />} />
          </FormField>
          <FormField label="Number" htmlFor="demo-number">
            <Input id="demo-number" type="number" placeholder="0" />
          </FormField>
          <FormField label="Date" htmlFor="demo-date">
            <Input id="demo-date" type="date" />
          </FormField>
          <FormField label="Search (clearable)" htmlFor="demo-search">
            <Input
              id="demo-search"
              type="search"
              leftIcon={<Search />}
              clearable
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              onClear={() => setSearchVal('')}
              placeholder="Szukaj..."
            />
          </FormField>
        </div>
      </Subsection>

      <Subsection n={2} title="Stany">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Normal" htmlFor="s-normal" helper="Helper tekst pod polem">
            <Input id="s-normal" placeholder="Wpisz coś" />
          </FormField>
          <FormField label="Z wartością" htmlFor="s-filled">
            <Input id="s-filled" defaultValue="Wypełnione" />
          </FormField>
          <FormField label="Disabled" htmlFor="s-disabled" helper="Nie można edytować">
            <Input id="s-disabled" defaultValue="Locked" disabled />
          </FormField>
          <FormField label="Error" htmlFor="s-error" error="Email jest zajęty">
            <Input id="s-error" type="email" defaultValue="jan@example.com" error />
          </FormField>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Select
// ============================================================================

export function SelectSection() {
  return (
    <div className="space-y-10">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Custom popup select (nie native HTML) - pełne stylowanie listy opcji, grupowanie, klawiatura
        ↑↓ Enter Esc, click outside zamyka, focus visible. Dwa warianty:{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Select>'}</code>{' '}
        — płaska lista,{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<GroupedSelect>'}</code>{' '}
        — sekcje z nagłówkami (per sklep, kategoria itd.). Oba akceptują children jako native{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<option>'}</code>{' '}
        / <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<optgroup>'}</code>.
      </p>

      <CodeSnippet
        language="tsx"
        code={`import { Select } from '@/components/ui/Select';
import { GroupedSelect } from '@/components/ui/GroupedSelect';

// Wariant 1: prosta lista
<Select value={country} onChange={(e) => setCountry(e.target.value)}>
  <option value="">-- Wybierz kraj --</option>
  <option value="pl">Polska</option>
  <option value="de">Niemcy</option>
</Select>

// Wariant 2: sekcje per grupa (sklep, kategoria, etc.)
<GroupedSelect value={statusId} onChange={(e) => setStatusId(e.target.value)}>
  <option value="">-- Wybierz status --</option>
  <optgroup label="LEMON GLOW">
    <option value="cml1">Nowe</option>
    <option value="cml2">W realizacji</option>
  </optgroup>
  <optgroup label="MEDIVERA">
    <option value="cml3">Nowe</option>
  </optgroup>
</GroupedSelect>`}
      />

      <Subsection n={1} title="Select - prosta lista">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Z placeholderem" htmlFor="sel-placeholder">
            <Select id="sel-placeholder" defaultValue="">
              <option value="">-- Wybierz kraj --</option>
              <option value="pl">Polska</option>
              <option value="de">Niemcy</option>
              <option value="cz">Czechy</option>
              <option value="sk">Słowacja</option>
            </Select>
          </FormField>
          <FormField label="Z wartością" htmlFor="sel-filled">
            <Select id="sel-filled" defaultValue="pl">
              <option value="pl">Polska</option>
              <option value="de">Niemcy</option>
            </Select>
          </FormField>
          <FormField label="Disabled" htmlFor="sel-disabled">
            <Select id="sel-disabled" defaultValue="pl" disabled>
              <option value="pl">Polska</option>
            </Select>
          </FormField>
          <FormField label="Error" htmlFor="sel-error" error="Wybierz kraj dostawy">
            <Select id="sel-error" defaultValue="" error>
              <option value="">-</option>
              <option value="pl">Polska</option>
            </Select>
          </FormField>
        </div>
      </Subsection>

      <Subsection n={2} title="GroupedSelect - sekcje per grupa">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Status zamówienia" htmlFor="gsel-status" helper="Sekcje per sklep z separatorami">
            <GroupedSelect id="gsel-status" defaultValue="">
              <option value="">-- Wybierz status --</option>
              <optgroup label="LEMON GLOW">
                <option value="lg-new">Nowe</option>
                <option value="lg-progress">W realizacji</option>
                <option value="lg-done">Wysłane</option>
              </optgroup>
              <optgroup label="MEDIVERA">
                <option value="med-new">Nowe</option>
                <option value="med-progress">W realizacji - FM</option>
                <option value="med-done">Wysłane</option>
              </optgroup>
              <optgroup label="HEADI">
                <option value="hd-new">Nowe</option>
                <option value="hd-done">Wysłane</option>
              </optgroup>
              <optgroup label="INNE">
                <option value="dup">Duplikaty</option>
              </optgroup>
            </GroupedSelect>
          </FormField>
          <FormField label="Z preselekcją" htmlFor="gsel-filled">
            <GroupedSelect id="gsel-filled" defaultValue="med-progress">
              <optgroup label="LEMON GLOW">
                <option value="lg-new">Nowe</option>
              </optgroup>
              <optgroup label="MEDIVERA">
                <option value="med-new">Nowe</option>
                <option value="med-progress">W realizacji - FM</option>
              </optgroup>
            </GroupedSelect>
          </FormField>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Textarea
// ============================================================================

export function TextareaSection() {
  const [autoVal, setAutoVal] = useState('Wpisz kilka linii...\nAuto-resize rośnie w miarę pisania.');
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Komponent <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Textarea>'}</code>.
        Default = standard multi-line input. Z{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">autoResize</code>{' '}
        rośnie do <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">maxRows</code>.
      </p>
      <Subsection n={1} title="Warianty i stany">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Standard (rows=4)" htmlFor="ta-std">
            <Textarea id="ta-std" rows={4} placeholder="Pełna notatka..." />
          </FormField>
          <FormField label="Auto-resize (min 2, max 8)" htmlFor="ta-auto" helper="Rośnie do 8 linii, potem scroll">
            <Textarea
              id="ta-auto"
              autoResize
              minRows={2}
              maxRows={8}
              value={autoVal}
              onChange={(e) => setAutoVal(e.target.value)}
            />
          </FormField>
          <FormField label="Disabled" htmlFor="ta-dis">
            <Textarea id="ta-dis" rows={3} defaultValue="Locked content" disabled />
          </FormField>
          <FormField label="Error" htmlFor="ta-err" error="Opis jest wymagany">
            <Textarea id="ta-err" rows={3} defaultValue="" error />
          </FormField>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Checkbox
// ============================================================================

export function CheckboxSection() {
  const [a, setA] = useState(false);
  const [b, setB] = useState(true);
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Custom checkbox - natywny input pod spodem (accessibility), wizualna replika wygląda jednolicie
        w dark mode. Etykieta klikalna, peer-focus-visible ring.
      </p>
      <Subsection n={1} title="Warianty i stany">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Checkbox
              label="Zgoda marketingowa"
              description="Będziemy wysyłać informacje o promocjach (max 1x/tydzień)."
              checked={a}
              onChange={(e) => setA(e.target.checked)}
            />
            <Checkbox
              label="Checked default"
              checked={b}
              onChange={(e) => setB(e.target.checked)}
            />
            <Checkbox label="Bez description" checked={false} readOnly />
            <Checkbox label="Disabled unchecked" disabled />
            <Checkbox label="Disabled checked" checked readOnly disabled />
            <Checkbox
              label="Error state"
              description="Musisz zaakceptować regulamin"
              error
            />
          </div>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Radio
// ============================================================================

export function RadioSection() {
  const [plan, setPlan] = useState<'basic' | 'pro' | 'enterprise'>('pro');
  const [orient, setOrient] = useState<'yes' | 'no'>('yes');

  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Radio + RadioGroup. RadioGroup zarządza value/onChange dla listy opcji.
        Pojedyncze <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Radio>'}</code>{' '}
        też dostępne dla custom layoutu.
      </p>

      <Subsection n={1} title="Vertical (default) z description">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <RadioGroup
            name="plan"
            value={plan}
            onChange={setPlan}
            options={[
              { value: 'basic', label: 'Basic', description: 'Do 100 zamówień/mies.' },
              { value: 'pro', label: 'Pro', description: 'Do 1000 zamówień/mies. + API.' },
              { value: 'enterprise', label: 'Enterprise', description: 'Bez limitów + SLA.' },
            ]}
          />
        </div>
      </Subsection>

      <Subsection n={2} title="Horizontal">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <RadioGroup
            name="orient"
            value={orient}
            onChange={setOrient}
            orientation="horizontal"
            options={[
              { value: 'yes', label: 'Tak' },
              { value: 'no', label: 'Nie' },
            ]}
          />
        </div>
      </Subsection>

      <Subsection n={3} title="Disabled">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <RadioGroup
            name="dis"
            value="a"
            orientation="horizontal"
            disabled
            options={[
              { value: 'a', label: 'Opcja A' },
              { value: 'b', label: 'Opcja B' },
            ]}
          />
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Toggle
// ============================================================================

export function ToggleSection() {
  const [a, setA] = useState(true);
  const [b, setB] = useState(false);
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        iOS-style pill switch. Do zastosowań gdzie checkbox byłby zbyt "formularzowy" (settings toggles,
        enable/disable features). Rozmiary: <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">sm</code>{' '}
        i <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">md</code>.
      </p>

      <Subsection n={1} title="Warianty i stany">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-5 space-y-7">
          <Toggle
            label="Powiadomienia email"
            description="Wysyłaj powiadomienia o nowych zamówieniach na maila."
            checked={a}
            onChange={(e) => setA(e.target.checked)}
          />
          <Toggle
            label="Dual theme"
            description="Tryb ciemny/jasny — przełączasz w prawym górnym rogu."
            checked={b}
            onChange={(e) => setB(e.target.checked)}
          />
          <Toggle size="sm" label="Small size" checked readOnly />
          <Toggle label="Disabled on" checked readOnly disabled />
          <Toggle label="Disabled off" disabled />
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: ToggleRow
// ============================================================================

export function ToggleRowSection() {
  const [q, setQ] = useState(true);
  const [d, setD] = useState(true);
  const [a, setA] = useState(false);
  const toast = useToast();

  return (
    <div className="space-y-6">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<ToggleRow>'}</code>{' '}
        — wiersz ustawień: label + description po lewej, toggle (lub action) po prawej. Łącz z{' '}
        <code className="px-1 rounded bg-surface-raised text-text-secondary text-[11px]">{'<SettingsSection flushContent>'}</code>{' '}
        dla listy zero-padding.
      </p>

      <CodeSnippet
        language="tsx"
        code={`import { SettingsSection } from '@/components/ui/SettingsSection';
import { ToggleRow } from '@/components/ui/ToggleRow';

<SettingsSection icon={Bell} title="Powiadomienia" flushContent>
  <ToggleRow
    label="Email alerts"
    description="Wyślemy maila przy ważnych zdarzeniach"
    checked={enabled}
    onChange={setEnabled}
  />
  <ToggleRow
    label="Wyczyść stare drafty"
    description="Kliknij, żeby wyczyścić teraz"
    action={<Button size="sm" variant="secondary">Wyczyść</Button>}
  />
</SettingsSection>`}
      />

      <Subsection n={1} title="Lista w SettingsSection">
      <SettingsSection
        icon={Rows3}
        title="Automatyzacje AI"
        subtitle="Reguły kolejki i draftów"
        flushContent
      >
        <ToggleRow
          label="Kolejka do przeglądu"
          description="Wiadomości o średniej pewności trafiają do ręcznego przeglądu"
          checked={q}
          onChange={setQ}
        />
        <ToggleRow
          label="Drafty AI tylko dla Skrzynki odbiorczej"
          description="AI nie generuje draftów dla wiadomości w folderach własnych ani w Archiwum"
          checked={d}
          onChange={setD}
        />
        <ToggleRow
          label="Auto-oznacz jako Rozwiązane po wysłaniu draftu"
          description="Po kliknięciu 'Zatwierdź i wyślij' wątek automatycznie dostaje status 'Rozwiązane'"
          warning='Wymaga zmapowania statusu „Rozwiązane" do folderu w zakładce Statusy konwersacji.'
          checked={a}
          onChange={setA}
        />
        <ToggleRow
          label="Usuń draft AI gdy wiadomość znika z Inboxa"
          description="Gdy mail zostaje przeniesiony do Kosza, Archiwum lub własnego folderu"
          defaultChecked
        />
        <ToggleRow
          label="Masz już stare drafty dla maili poza Inboxem?"
          description="Kliknij, żeby wyczyścić teraz."
          action={<Button size="sm" variant="secondary" onClick={() => toast.info('Kliknięto Wyczyść teraz')}>Wyczyść teraz</Button>}
        />
        <ToggleRow
          label="Disabled toggle"
          description="Nie można zmienić w tym kontekście"
          disabled
          checked
        />
      </SettingsSection>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Slider + BreakpointSlider
// ============================================================================

export function SliderSection() {
  const [simple, setSimple] = useState(50);
  const [padding, setPadding] = useState({ desktop: 20, tablet: 16, mobile: 12 });

  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Slider>'}</code>{' '}
        — numeric range (native input). {' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<BreakpointSlider>'}</code>{' '}
        — 3 slidery Desktop/Tablet/Mobile obok siebie (wzorzec z Kreator).
      </p>

      <Subsection n={1} title="Simple slider">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-5 space-y-5">
          <Slider value={simple} onChange={setSimple} unit="%" />
          <Slider value={simple} onChange={setSimple} label="Opacity" unit="%" />
          <Slider value={simple} onChange={setSimple} icon={Sparkles} label="Z ikoną" unit="%" />
        </div>
      </Subsection>

      <Subsection n={2} title="BreakpointSlider (Desktop/Tablet/Mobile)">
        <SettingsSection
          icon={Sliders}
          title="Wcięcie kontenera (lewo/prawo)"
          subtitle="Odstęp treści od krawędzi strony. Dotyczy wszystkich sekcji."
          dense
        >
          <BreakpointSlider
            value={padding}
            onChange={setPadding}
            min={0}
            max={80}
            unit="px"
          />
        </SettingsSection>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: CategoryAutoActionRow (konfiguracja auto-akcji per kategoria)
// ============================================================================

export function CategoryAutoActionRowSection() {
  const [cfg, setCfg] = useState<Record<string, { priority: number; autoAction: string | null }>>({
    tracking: { priority: 50, autoAction: null },
    return: { priority: 70, autoAction: 'flag' },
  });
  const actionOptions = [
    { value: 'none', label: 'Brak' },
    { value: 'auto_resolve', label: 'Auto-rozwiąż' },
    { value: 'flag', label: 'Oflaguj' },
  ];

  return (
    <div className="space-y-6">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<CategoryAutoActionRow>'}</code>{' '}
        — wiersz konfiguracji auto-akcji dla kategorii (pill kategorii + priority
        input + action select). Użycie: N-krotne w gridzie po kategoriach (AI
        kategorie w Ustawieniach Wiadomości). Paleta per-kategoria (badgeClassName)
        pozostaje po stronie użytkownika — to świadoma semantyka biznesowa.
      </p>

      <Subsection n={1} title="2 kategorie w gridzie">
        <CodeSnippet
          className="mb-3"
          language="tsx"
          code={`<CategoryAutoActionRow
  badgeLabel="Tracking"
  badgeClassName="text-accent-info bg-info/10 border-info/30"
  priority={cfg.priority}
  onPriorityChange={(v) => setCfg({ ...cfg, priority: v })}
  autoAction={cfg.autoAction}
  onAutoActionChange={(v) => setCfg({ ...cfg, autoAction: v })}
  options={[
    { value: 'none', label: 'Brak' },
    { value: 'auto_resolve', label: 'Auto-rozwiąż' },
    { value: 'flag', label: 'Oflaguj' },
  ]}
/>`}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <CategoryAutoActionRow
            badgeLabel="Tracking"
            badgeClassName="text-accent-info bg-info/10 border-info/30"
            priority={cfg.tracking.priority}
            onPriorityChange={(v) => setCfg(p => ({ ...p, tracking: { ...p.tracking, priority: v } }))}
            autoAction={cfg.tracking.autoAction}
            onAutoActionChange={(v) => setCfg(p => ({ ...p, tracking: { ...p.tracking, autoAction: v } }))}
            options={actionOptions}
          />
          <CategoryAutoActionRow
            badgeLabel="Zwrot"
            badgeClassName="text-warning bg-warning/10 border-warning/30"
            priority={cfg.return.priority}
            onPriorityChange={(v) => setCfg(p => ({ ...p, return: { ...p.return, priority: v } }))}
            autoAction={cfg.return.autoAction}
            onAutoActionChange={(v) => setCfg(p => ({ ...p, return: { ...p.return, autoAction: v } }))}
            options={actionOptions}
            saving
          />
        </div>
      </Subsection>
    </div>
  );
}
